<script setup lang="ts">
import {
  formatting,
  LegacyAddress,
  LegacyDelivery,
  LegacyOrder,
  legacyOrderKey,
  useDialogs,
  useLocalization
} from '@hantera/portal-app';
import {ContextMenuSections, FloatingPanel} from '@hantera/design-system';
import {computed, inject, ref, Ref, watch} from 'vue';
import AddressEditor from '@/components/LegacyAddressEditor.vue';
import useBackend from '@/composition/useBackend';
import AddressDialog from './LegacyAddressDialog.vue';

const props = defineProps<{
  address: LegacyAddress
  deliveries: LegacyDelivery[]
  isInvoiceAddress: boolean
}>();

const { country } = formatting;
const t = useLocalization();
const order = inject(legacyOrderKey, <Ref<LegacyOrder>>{});
const editorDialog = ref<typeof FloatingPanel>();
const addressEditor = ref<typeof AddressEditor>();
const backend = useBackend();
const dialogs = useDialogs();

const assignTo = ref<{
  delivery: LegacyDelivery
  assign: boolean
}[]>([]);
const assignToInvoiceAddress = ref(false);

const editingAddress = ref(props.address);
watch(() => props.address, val => editingAddress.value = val, { deep: true });
watch(() => props.isInvoiceAddress, () => assignToInvoiceAddress.value = props.isInvoiceAddress, { immediate: true });

const displayName = computed(() => {
  return order.value.customer.customerName !== `${props.address.firstName} ${props.address.lastName}`;
});
const displayEmail = computed(() => {
  return order.value.customer.email !== props.address.email;
});
const displayPhone = computed(() => {
  return order.value.customer.phoneNumber !== props.address.phoneNumber;
});

const dialog = dialogs.add(t('orderView.editAddressTitle'), AddressDialog, true, {
  order: order.value,
  address: props.address,
  deliveries: props.deliveries,
  isInvoiceAddress: props.isInvoiceAddress,
});

function openEdit() {
  dialog.openModal();
}

const contextMenu = <ContextMenuSections>[[{
  label: 'Copy address',
  action: () => {

    let result = props.address.firstName + ' ' + props.address.lastName;

    if (props.address.careOf) {
      result += `\nc/o ${props.address.careOf})`
    }
    result += `\n${props.address.address1}`
    if (props.address.address2) {
      result += `\n${props.address.address2}`
    }

    if (props.address.countryCode === 'US') {
      result += `\n${props.address.city}, ${props.address.state} ${props.address.postalCode}`
    } else {
      result += `\n${props.address.postalCode} ${props.address.city}`

      if (props.address.state) {
        result += `, ${props.address.state}`
      }
    }

    result += `\n${country(props.address.countryCode)}`

    if (props.address.email || props.address.phoneNumber) {
      result += `\n`
    }
    if (props.address.email) {
      result += `\nE-mail: ${props.address.email}`
    }
    if (props.address.phoneNumber) {
      result += `\nPhone: ${props.address.phoneNumber}`
    }

    navigator.clipboard.writeText(result);
  }
}]]

</script>

<template>
  <div class="order-address">
    <div class="order-address_address" v-context-menu="contextMenu">
      <div v-if="displayName">{{ address.firstName }} {{ address.lastName }}</div>
      <div>{{ address.address1 }}</div>
      <div>{{ address.address2 }}</div>
      <div>{{ address.postalCode }} {{ address.city }}<span v-if="address.state">, {{
        address.state }}</span>
      </div>
      <div>{{ country(address.countryCode) }}</div>
      <div v-if="displayEmail">{{ address.email }}</div>
      <div v-if="displayPhone">{{ address.phoneNumber }}</div>
      <div class="order-address__tags">
        <div
          v-if="isInvoiceAddress"
          class="order-address__tag order-address__invoice-address-tag"
        >{{ t('orderView.invoiceAddress') }}</div>
        <div
          v-for="delivery in deliveries"
          class="order-address__tag"
          :style="`border-color: var(--state-colors__delivery-${delivery.state.toLowerCase()})`"
        >{{ delivery.deliveryNumber }}</div>
      </div>
    </div>

    <div class="order-address__edit-container">
      <button
        @click="openEdit"
      >{{ t('common.edit') }}</button>
    </div>
  </div>
</template>

<style>
.order-address {
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: auto min-content;
  gap: 10px;
  border-bottom: 1px solid hsl(var(--color-70));
  padding: 5px;
}

.order-address:last-child {
  border-bottom: none;
}

.order-address__edit-container {
  display: flex;
  align-items: center;
}

.order-address__tags {
  display: flex;
  gap: 5px;
  font-size: 12px;
  flex-wrap: wrap;
}

.order-address__tag {
  padding: 3px 3px 1px 3px;
  margin-top: 2px;
  border-radius: 4px;
  border-width: 1px;
  border-style: solid;
}

.order-address__invoice-address-tag {
  background-color: hsl(var(--color-70));
  border-radius: 4px;
  border-color: hsl(var(--color-70));
}
</style>